import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import InlineForm from 'components/forms/InlineForm';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { saveData } from 'services/api/saveData';
import { apiBaseUrl } from 'App';
import { FieldType } from 'types/FieldTypes';
import { FormButtonType } from 'types/ButtonTypes';
import { v4 as uuidv4 } from 'uuid';

const SetPasswordForm: React.FC = () => {
    const viewKey = useMemo(() => uuidv4(), []);
    const history = useHistory();
    const { t } = useTranslation();

    // Get the token from the route params
    const { token } = useParams<{ token: string }>();

    // Store the state of the migration set password token
    const [tokenState, setTokenState] = useState<string | null>(null);

    // Store the email address of the account the password is set for
    const [email, setEmail] = useState<string | null>(null);

    // Validate the token on the server when the page loads
    useEffect(() => {
        const validateToken = async () => {
            try {
                const response = await axios.get(`${apiBaseUrl}/get_validate_migration_set_password_token/${token}/`);
                setTokenState(response.data.state);
                setEmail(response.data.email || null);
            } catch (error) {
                setTokenState('invalid');
            }
        };
        validateToken();
    }, [token]);

    // Redirect to the login page when the password is already set
    useEffect(() => {
        if (tokenState === 'password_already_set') {
            history.replace('/login');
        }
    }, [tokenState, history]);

    const formHeader = 'account.set_password_header_label'
    const formDescription = 'account.set_password_header_description'

    const fields: FieldType[] = [
        {
            type: 'password',
            name: 'password',
            placeholder: 'account.reset_password_new_password_placeholder',
        },
        {
            type: 'password',
            name: 'password_confirm',
            placeholder: 'account.reset_password_confirm_password_placeholder',
        },
    ];

    const buttons: FormButtonType[] = [
        {
            label: 'account.set_password_button_label',
            position: 'fullwidth',
            hierarchy: 'primary',
            onClick: 'handleSubmit',
            size: 'large',
            showLoader: true,
        },
    ];

    // Submits the new password together with the token
    const handleSetPassword = async (data: Record<string, any>) => {

        // Check that both entered passwords match
        if (data.password !== data.password_confirm) {
            throw { response: { data: { password_confirm: 'account.reset_password_mismatch' } } };
        }

        // Save the new password on the server
        await saveData({
            apiUrl: 'post_migration_set_password',
            method: 'post',
            data: { token, password: data.password },
        });

        // Redirect to the login page after a successful set
        history.push('/login?password_reset=success');
    };

    // Show nothing while the token is being validated or redirected
    if (tokenState === null || tokenState === 'password_already_set') {
        return null;
    }

    // Show a message with the forgot password fallback for unusable tokens
    if (tokenState !== 'valid') {
        return (
            <div className='set-password-invalid'>
                <h4>{t('account.set_password_invalid_header_label')}</h4>
                <p>{t('account.set_password_invalid_description')}</p>
                <SecondaryButton
                    label="account.set_password_invalid_forgot_button_label"
                    size="small"
                    route="/password-forgotten" />
            </div>
        );
    }

    return (
        <>
            {email &&
                <div className='set-password-email'>
                    <span className='set-password-email-label'>{t('account.set_password_email_label')}</span>
                    <span className='set-password-email-value'>{email}</span>
                </div>
            }
            <InlineForm
                viewKey={viewKey}
                customApiFunction={handleSetPassword}
                formHeader={formHeader}
                formDescription={formDescription}
                fields={fields}
                buttons={buttons}
            />
        </>
    );
};

export default SetPasswordForm;
